import { Link } from 'react-router-dom';
import type { HostProfileStrength } from '../../../lib/hostProfileStrength';

interface ProfileStatusBannerProps {
  strength: HostProfileStrength;
}

/**
 * The first thing a host should know: are they actually being matched?
 *
 * Nothing in the app used to say this. A host with no billing
 * details is filtered out of alma-finalize's candidate query entirely — they
 * receive no work at all — yet the only hint was a calm grey sentence in the
 * last card on the page. This states it plainly, at the top, with the specific
 * reasons and where to fix each one.
 */
export function ProfileStatusBanner({ strength }: ProfileStatusBannerProps) {
  if (!strength.matchable) {
    return (
      <div className="rounded-2xl border border-crimson/30 bg-crimson/10 p-5 mb-8" role="status">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-crimson/15 flex items-center justify-center shrink-0">
            <i className="ti ti-alert-triangle text-crimson text-base" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-app-fg">You're not being matched yet</p>
            <p className="text-xs text-app-muted mt-1 leading-relaxed">
              Travelers can't be matched with you until these are done. Everything else on this
              page makes your profile better — these decide whether you appear at all.
            </p>
            <ul className="flex flex-col gap-1.5 mt-3">
              {strength.blockers.map((b) => (
                <li key={b.key} className="flex items-center gap-2 text-xs text-app-fg-2">
                  <i className="ti ti-circle text-crimson text-[10px]" aria-hidden="true" />
                  {b.key === 'payouts' ? (
                    <span>
                      {b.label} —{' '}
                      <Link to="/host/earnings" className="text-crimson underline underline-offset-2">
                        add billing details
                      </Link>
                    </span>
                  ) : (
                    <span>{b.label}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  const remaining = [...strength.ranking.items, ...strength.winning.items].filter((i) => !i.done);

  return (
    <div className="rounded-2xl border border-app-border bg-app-card p-5 mb-8" role="status">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-success/15 flex items-center justify-center shrink-0">
          <i className="ti ti-check text-[--success-fg] text-base" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-semibold text-app-fg">You're live and can be matched</p>
            <span className="text-xs text-app-muted shrink-0">{strength.percent}% complete</span>
          </div>

          {/* Progress across all three tiers. */}
          <div className="h-1.5 rounded-full bg-app-bg border border-app-border mt-2.5 overflow-hidden">
            <div
              className="h-full bg-gold transition-[width] duration-500"
              style={{ width: `${strength.percent}%` }}
            />
          </div>

          {remaining.length > 0 ? (
            <p className="text-xs text-app-muted mt-3 leading-relaxed">
              {remaining.length === 1
                ? 'One thing left to strengthen your profile: '
                : `${remaining.length} things would strengthen your profile: `}
              <span className="text-app-fg-2">{remaining.map((r) => r.label.toLowerCase()).join(' · ')}</span>
            </p>
          ) : (
            <p className="text-xs text-app-muted mt-3">Your profile is complete. Nothing left to do.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileStatusBanner;
